import {
  Autocomplete,
  Box,
  Button,
  Divider,
  MenuItem,
  Typography,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { getAllSupplies } from "../helpers/getAllSupplies";
import { postSupplyProvider } from "../helpers/postSupplyProvider";
import { motion, AnimatePresence } from "framer-motion";
import { menuPropsModal } from "../../../../utils";
import { useModals } from "../../../../context/ModalContext";

export const AddSupplyXProvider = ({ idProvider, onClose, onSuccess, modalKey }) => {
  const [supplies, setSupplies] = useState([]);
  const token = sessionStorage.getItem("token");
  const { closeByKey } = useModals()

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      supply: null,
      name: "",
      type: "consumable",
      price: "",
      description: "",
    },
  });

  console.log(modalKey);

  const watchedSupply = watch("supply");

  useEffect(() => {
    const fetchSupplies = async () => {
      try {
        const res = await getAllSupplies({ token });
        setSupplies(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchSupplies();
  }, [token]);

  useEffect(() => {
    if (watchedSupply) {
      setValue("name", watchedSupply.name || "");
      setValue("type", watchedSupply.type || "consumable");
      setValue("price", "");
      setValue("description", "");
      watchedSupply.requiredProperties?.forEach((prop) => {
        setValue(prop.field, "");
      });
    } else {
      reset({
        supply: null,
        name: "",
        type: "consumable",
        price: "",
        description: "",
      });
    }
  }, [watchedSupply, setValue, reset]);

  const onSubmit = async (data) => {
    if (!data.supply) return;

    const payload = {
      supplyId: data.supply.id,
      providerId: idProvider,
      name: data.name,
      type: data.type,
      price: Number(data.price),
      description: data.description,
      requiredProperties: {},
    };

    watchedSupply.requiredProperties?.forEach((prop) => {
      payload.requiredProperties[prop.field] = Number(data[prop.field] || 0);
    });

    try {
      await postSupplyProvider({ token, data: payload });
      closeByKey(modalKey)
      onSuccess?.();
      onClose()
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 3,
        width: {
          xs: "95vw",
          sm: "80vw",
          md: "100%",
        },
        height: "80vh",
      }}
    >
      <Typography variant="h5" sx={{ mb: 4, fontWeight: "700", textAlign: "center" }}>
        Add Supply to Provider
      </Typography>

      {/* Selector supply */}
      <Controller
        name="supply"
        control={control}
        rules={{ required: "You must select a supply" }}
        render={({ field }) => (
          <Autocomplete
            disablePortal
            options={supplies}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={field.value}
            onChange={(_, newValue) => field.onChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a Supply"
                error={!!errors.supply}
                helperText={errors.supply?.message}
                fullWidth
                size="small"
                sx={{ mb: 4 }}
              />
            )}
          />
        )}
      />

      {/* Imagen animada */}
      <Box
        sx={{
          width: 190,
          height: 190,
          mx: "auto",
          mb: 1,
          borderRadius: 3,
          boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
          backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <AnimatePresence mode="wait">
          {watchedSupply?.image && (
            <motion.img
              key={watchedSupply.id}
              src={watchedSupply.image}
              alt={watchedSupply.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.35 }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
          )}
        </AnimatePresence>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {watchedSupply && (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 2, zIndex: 99999 }}
        >
          <TextField
            label="Name"
            {...register("name", { required: "Name is required" })}
            error={!!errors.name}
            helperText={errors.name?.message}
            fullWidth
            size="small"
          />

          {/* Aquí está el select para type usando Controller */}
          <Controller
            name="type"
            control={control}
            rules={{ required: "Type is required" }}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel id="type-select-label">Type</InputLabel>
                <Select MenuProps={menuPropsModal} labelId="type-select-label" label="Type" {...field}>
                  <MenuItem value="consumable">Consumable</MenuItem>
                  <MenuItem value="supply">Supply</MenuItem>
                </Select>
                {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
              </FormControl>
            )}
          />

          <TextField
            label="Price"
            type="number"
            inputProps={{ step: "0.01", min: 0 }}
            {...register("price", {
              required: "Price is required",
              min: { value: 0.01, message: "Price must be > 0" },
            })}
            error={!!errors.price}
            helperText={errors.price?.message}
            fullWidth
            size="small"
          />

          <TextField
            label="Description"
            multiline
            rows={2}
            {...register("description", {
              required: "Description is required",
            })}
            error={!!errors.description}
            helperText={errors.description?.message}
            fullWidth
            size="small"
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: "600" }}>
            Required Properties
          </Typography>

          {watchedSupply.requiredProperties?.map((prop) => (
            <TextField
              key={prop.field}
              label={prop.label}
              type="number"
              inputProps={{ step: "any", min: 0 }}
              {...register(prop.field, {
                required: `${prop.label} is required`,
                min: { value: 0, message: "Must be ≥ 0" },
              })}
              error={!!errors[prop.field]}
              helperText={errors[prop.field]?.message}
              fullWidth
              size="small"
            />
          ))}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2, mb: 6 }}>
            <Button onClick={onClose} sx={{ px: 4 }}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" sx={{ px: 4 }}>
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
